import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// A small colored dot + pill, scannable "at a glance" the way the Help
// guide's status legend describes — swapped in for the old plain colored
// parenthetical text, which read as an inline label rather than a status
// indicator. Same four mutually-exclusive states/colors as before.
function StatusBadge({ label, color, backgroundColor }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      marginLeft: '0.5rem',
      padding: '0.15rem 0.6rem 0.15rem 0.45rem',
      borderRadius: '999px',
      backgroundColor,
      color,
      fontSize: '0.75rem',
      fontWeight: 'bold',
      verticalAlign: 'middle'
    }}>
      <span style={{
        width: '0.5rem',
        height: '0.5rem',
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0
      }} />
      {label}
    </span>
  );
}

function OrderDates() {
  const { schoolId } = useParams();
  const apiBase = `/api/admin/${schoolId}`;
  const [orderDates, setOrderDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    open_date: '',
    delivery_date: '',
    close_date: '',
    close_time: '17:00', // Default to 5:00 PM
    is_active: true
  });

  const fetchOrderDates = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBase}/order-dates`);
      setOrderDates(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load order dates');
      setLoading(false);
    }
  }, [apiBase]);

  // Load order dates on component mount
  useEffect(() => {
    fetchOrderDates();
  }, [fetchOrderDates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Convert delivery date to ISO format at noon local time to avoid timezone issues
      const createDateFromInput = (dateString) => {
        const [year, month, day] = dateString.split('-');
        return new Date(year, month - 1, day, 12, 0, 0); // Set to noon local time to avoid timezone issues
      };

      // Combine close date and time in user's local timezone, then convert to UTC
      const createCloseDateFromInput = (dateString, timeString) => {
        const [year, month, day] = dateString.split('-');
        const [hours, minutes] = timeString.split(':');
        // Create date in user's local timezone
        return new Date(year, month - 1, day, parseInt(hours), parseInt(minutes), 0);
      };

      const submitData = {
        ...formData,
        open_date: createDateFromInput(formData.open_date).toISOString(),
        delivery_date: createDateFromInput(formData.delivery_date).toISOString(),
        close_date: createCloseDateFromInput(formData.close_date, formData.close_time).toISOString()
      };

      if (editingDate) {
        await axios.put(`${apiBase}/order-dates/${editingDate.id}`, submitData);
      } else {
        await axios.post(`${apiBase}/order-dates`, submitData);
      }

      // Reset form and refresh data
      setFormData({
        title: '',
        description: '',
        open_date: '',
        delivery_date: '',
        close_date: '',
        close_time: '17:00',
        is_active: true
      });
      setShowForm(false);
      setEditingDate(null);
      fetchOrderDates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save order date');
    }
  };

  const handleEdit = (orderDate) => {
    setEditingDate(orderDate);

    // Parse close date to extract date and time in user's local timezone
    const closeDateObj = new Date(orderDate.close_date);
    const hours = closeDateObj.getHours().toString().padStart(2, '0');
    const minutes = closeDateObj.getMinutes().toString().padStart(2, '0');

    setFormData({
      title: orderDate.title,
      description: orderDate.description,
      open_date: new Date(orderDate.open_date).toISOString().split('T')[0],
      delivery_date: new Date(orderDate.delivery_date).toISOString().split('T')[0],
      close_date: closeDateObj.toISOString().split('T')[0],
      close_time: `${hours}:${minutes}`,
      is_active: orderDate.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this order date?')) {
      return;
    }

    try {
      await axios.delete(`${apiBase}/order-dates/${id}`);
      fetchOrderDates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete order date');
    }
  };

  const handleCloseOrders = async (id, title) => {
    if (!window.confirm(`Are you sure you want to close orders for "${title}" immediately? You can move the close date forward again later via Edit if needed.`)) {
      return;
    }

    try {
      await axios.post(`${apiBase}/order-dates/${id}/close`);
      fetchOrderDates();
      setError(''); // Clear any existing errors
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close orders');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingDate(null);
    setFormData({
      title: '',
      description: '',
      open_date: '',
      delivery_date: '',
      close_date: '',
      close_time: '17:00',
      is_active: true
    });
    setError('');
  };

  // Helper function to suggest open date (30 days before delivery) and
  // close date (7 days before delivery)
  const handleDeliveryDateChange = (e) => {
    const deliveryDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      delivery_date: deliveryDate
    }));

    if (deliveryDate) {
      const delivery = new Date(deliveryDate);

      // Auto-calculate open date (30 days before delivery)
      const openDate = new Date(delivery);
      openDate.setDate(openDate.getDate() - 30);

      // Auto-calculate close date (7 days before delivery)
      const closeDate = new Date(delivery);
      closeDate.setDate(closeDate.getDate() - 7);

      setFormData(prev => ({
        ...prev,
        open_date: openDate.toISOString().split('T')[0],
        close_date: closeDate.toISOString().split('T')[0]
      }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isOrderWindowOpen = (orderDate) => {
    const now = new Date();
    const openDate = new Date(orderDate.open_date);
    const closeDate = new Date(orderDate.close_date);
    const deliveryDate = new Date(orderDate.delivery_date);

    return now >= openDate && now < closeDate && now < deliveryDate;
  };

  const isNotYetOpen = (orderDate) => new Date(orderDate.open_date) > new Date();

  // "Closed" means the order window has already passed — those sink to the
  // bottom so admins see upcoming/open dates first. Within each group, keep
  // ordering by delivery date (soonest first) like the API already returns.
  const isClosed = (orderDate) => new Date(orderDate.close_date) < new Date();

  const sortedOrderDates = [...orderDates].sort((a, b) => {
    const aClosed = isClosed(a);
    const bClosed = isClosed(b);
    if (aClosed !== bClosed) {
      return aClosed ? 1 : -1;
    }
    if (aClosed) {
      // Closed dates: most recently closed first (descending delivery
      // date), so the lunch day that just happened is the first thing
      // under the "Closed Order Dates" divider rather than buried at
      // the bottom under months of history.
      return new Date(b.delivery_date) - new Date(a.delivery_date);
    }
    // Still-open dates: soonest delivery first, so the one you're
    // actively running always stays right at the top.
    return new Date(a.delivery_date) - new Date(b.delivery_date);
  });

  if (loading) {
    return <div>Loading order dates...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Order Dates Management</h2>
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Add New Order Date
        </button>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3>{editingDate ? 'Edit Order Date' : 'Add New Order Date'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Title <span style={{ fontWeight: 'normal', color: '#666' }}>(optional)</span>:
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., October Lunch Orders"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Description:
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Delivery Date:
                </label>
                <input
                  type="date"
                  value={formData.delivery_date}
                  onChange={handleDeliveryDateChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Order Window Open Date:
                </label>
                <input
                  type="date"
                  value={formData.open_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, open_date: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '0.8rem', display: 'block', marginTop: '0.5rem' }}>
                  Date automatically set to 30 days before delivery. Orders can't be placed before this date.
                </small>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Order Window Close Date:
                </label>
                <input
                  type="date"
                  value={formData.close_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, close_date: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    marginBottom: '0.5rem'
                  }}
                />
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Order Window Close Time:
                </label>
                <input
                  type="time"
                  value={formData.close_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, close_time: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '0.8rem', display: 'block', marginTop: '0.5rem' }}>
                  Date automatically set to 7 days before delivery. Time is in your local timezone.
                </small>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span style={{ fontWeight: 'bold' }}>Active</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {editingDate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {orderDates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No order dates configured yet. Click "Add New Order Date" to get started.
          </div>
        ) : (
          sortedOrderDates.map((orderDate, index) => (
            <React.Fragment key={orderDate.id}>
              {isClosed(orderDate) && (index === 0 || !isClosed(sortedOrderDates[index - 1])) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  margin: '0.5rem 0'
                }}>
                  <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #ddd' }} />
                  <span style={{ color: '#999', fontSize: '0.8rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Closed Order Dates
                  </span>
                  <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #ddd' }} />
                </div>
              )}
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  backgroundColor: orderDate.is_active ? 'white' : '#f8f9fa'
                }}
              >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: orderDate.is_active ? '#333' : '#666', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    {orderDate.title || formatDate(orderDate.delivery_date)}
                    {!orderDate.is_active && (
                      <StatusBadge label="Inactive" color="#dc3545" backgroundColor="#fbe4e6" />
                    )}
                    {isOrderWindowOpen(orderDate) && orderDate.is_active && (
                      <StatusBadge label="Orders Open" color="#28a745" backgroundColor="#e3f5e8" />
                    )}
                    {isClosed(orderDate) && (
                      <StatusBadge label="Closed" color="#6c757d" backgroundColor="#eceeef" />
                    )}
                    {isNotYetOpen(orderDate) && orderDate.is_active && (
                      <StatusBadge label="Not Yet Open" color="#fd7e14" backgroundColor="#fef0e1" />
                    )}
                  </h3>
                  {orderDate.description && (
                    <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
                      {orderDate.description}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleEdit(orderDate)}
                    style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Edit
                  </button>
                  {/* Show Close Orders button only if orders are currently open */}
                  {isOrderWindowOpen(orderDate) && orderDate.is_active && (
                    <button
                      onClick={() => handleCloseOrders(orderDate.id, orderDate.title)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#fd7e14',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Close Orders Now
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(orderDate.id)}
                    disabled={orderDate.order_count > 0}
                    title={orderDate.order_count > 0
                      ? `Can't delete — ${orderDate.order_count} order${orderDate.order_count === 1 ? '' : 's'} attached`
                      : undefined}
                    style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: orderDate.order_count > 0 ? '#e9a8ae' : '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: orderDate.order_count > 0 ? 'not-allowed' : 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <strong>Orders Open:</strong><br />
                  <span style={{ color: isNotYetOpen(orderDate) ? '#fd7e14' : '#666' }}>
                    {formatDate(orderDate.open_date)}
                  </span>
                </div>
                <div>
                  <strong>Delivery Date:</strong><br />
                  <span style={{ color: '#007bff' }}>
                    {formatDate(orderDate.delivery_date)}
                  </span>
                </div>
                <div>
                  <strong>Orders Close:</strong><br />
                  <span style={{ color: new Date(orderDate.close_date) < new Date() ? '#dc3545' : '#28a745' }}>
                    {formatDateTime(orderDate.close_date)}
                  </span>
                </div>
              </div>

              <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                Created: {formatDate(orderDate.created_at)}
                {orderDate.updated_at !== orderDate.created_at && (
                  <span> • Updated: {formatDate(orderDate.updated_at)}</span>
                )}
                {orderDate.order_count > 0 && (
                  <span> • {orderDate.order_count} order{orderDate.order_count === 1 ? '' : 's'} attached</span>
                )}
              </div>
            </div>
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
}

export default OrderDates;
